import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, RefreshCcw } from "lucide-react";

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [editing, setEditing] = useState({}); // { [plan_id]: { fields } }
  const [saving, setSaving] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/plans");
    setPlans(data.plans);
  };

  useEffect(() => { load(); }, []);

  const setField = (id, field, value) => {
    setEditing((e) => ({ ...e, [id]: { ...(e[id] || {}), [field]: value } }));
  };
  const valueOf = (plan, field) => {
    const e = editing[plan.id] || {};
    return field in e ? e[field] : plan[field];
  };
  const save = async (planId) => {
    const patch = editing[planId] || {};
    if (!Object.keys(patch).length) return;
    setSaving((s) => ({ ...s, [planId]: true }));
    try {
      // Convert features text -> array
      if (typeof patch.features_fr === "string") patch.features_fr = patch.features_fr.split("\n").map((s) => s.trim()).filter(Boolean);
      if (typeof patch.features_en === "string") patch.features_en = patch.features_en.split("\n").map((s) => s.trim()).filter(Boolean);
      if (typeof patch.price_eur === "string") patch.price_eur = parseFloat(patch.price_eur);
      if (typeof patch.quota_gb === "string") patch.quota_gb = patch.quota_gb === "" ? null : parseInt(patch.quota_gb, 10);
      if (typeof patch.period_days === "string") patch.period_days = parseInt(patch.period_days, 10);
      await api.put(`/admin/plans/${planId}`, patch);
      toast.success("Plan mis à jour");
      setEditing((e) => { const c = { ...e }; delete c[planId]; return c; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving((s) => ({ ...s, [planId]: false }));
    }
  };

  if (!plans) return <div className="font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="admin-plans-page">
      <div className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// Plans</div>
          <h1 className="mt-1 font-display text-2xl font-black tracking-tight">Tarifs et plans</h1>
          <p className="mt-1 text-sm text-muted-foreground">Modifiez les prix, quotas et fonctionnalités. Les changements s'appliquent immédiatement à la page Tarifs publique.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm gap-2" data-testid="admin-plans-reload"><RefreshCcw className="h-4 w-4" /> Recharger</Button>
      </div>

      <div className="mt-6 space-y-6">
        {plans.map((p) => {
          const isDirty = Boolean(editing[p.id] && Object.keys(editing[p.id]).length);
          const featuresFrText = typeof editing[p.id]?.features_fr === "string"
            ? editing[p.id].features_fr
            : (p.features_fr || []).join("\n");
          const featuresEnText = typeof editing[p.id]?.features_en === "string"
            ? editing[p.id].features_en
            : (p.features_en || []).join("\n");
          return (
            <div key={p.id} className="border border-border bg-card p-5" data-testid={`admin-plan-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">/{p.id}</span>
                  <h2 className="font-display text-lg font-bold tracking-tight">{p.label}</h2>
                  {p.one_shot && <span className="border border-[#002FA7] bg-[#002FA7]/10 px-2 py-0.5 font-mono text-[10px] uppercase text-[#002FA7]">one-shot</span>}
                  {p.unlimited && <span className="border border-emerald-500 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] uppercase text-emerald-700">unlimited</span>}
                </div>
                <div className="flex items-center gap-3">
                  <Label className="flex items-center gap-2 font-mono text-xs">
                    <Switch checked={Boolean(valueOf(p, "is_active") ?? true)} onCheckedChange={(v) => setField(p.id, "is_active", v)} data-testid={`admin-plan-${p.id}-active`} />
                    Actif
                  </Label>
                </div>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div>
                  <Label className="font-mono text-[11px] uppercase text-muted-foreground">Label</Label>
                  <Input className="mt-1 rounded-sm" value={valueOf(p, "label") || ""} onChange={(e) => setField(p.id, "label", e.target.value)} data-testid={`admin-plan-${p.id}-label`} />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase text-muted-foreground">Prix (€)</Label>
                  <Input type="number" step="0.01" className="mt-1 rounded-sm" value={String(valueOf(p, "price_eur") ?? 0)} onChange={(e) => setField(p.id, "price_eur", e.target.value)} data-testid={`admin-plan-${p.id}-price`} />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase text-muted-foreground">Quota (Go) — vide = illimité</Label>
                  <Input type="number" className="mt-1 rounded-sm" value={valueOf(p, "quota_gb") == null ? "" : String(valueOf(p, "quota_gb"))} onChange={(e) => setField(p.id, "quota_gb", e.target.value)} data-testid={`admin-plan-${p.id}-quota`} />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase text-muted-foreground">Période (jours)</Label>
                  <Input type="number" className="mt-1 rounded-sm" value={String(valueOf(p, "period_days") ?? 30)} onChange={(e) => setField(p.id, "period_days", e.target.value)} data-testid={`admin-plan-${p.id}-period`} />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase text-muted-foreground">Stripe Price ID (optionnel)</Label>
                  <Input className="mt-1 rounded-sm font-mono text-xs" value={valueOf(p, "stripe_price_id") || ""} onChange={(e) => setField(p.id, "stripe_price_id", e.target.value)} placeholder="price_xxx" data-testid={`admin-plan-${p.id}-stripe`} />
                </div>
                <div className="flex items-end">
                  <Label className="flex items-center gap-2 font-mono text-xs">
                    <Switch checked={Boolean(valueOf(p, "unlimited"))} onCheckedChange={(v) => setField(p.id, "unlimited", v)} data-testid={`admin-plan-${p.id}-unlimited`} />
                    Illimité
                  </Label>
                </div>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="font-mono text-[11px] uppercase text-muted-foreground">Features (FR) — une par ligne</Label>
                  <Textarea rows={6} className="mt-1 rounded-sm font-mono text-xs" value={featuresFrText} onChange={(e) => setField(p.id, "features_fr", e.target.value)} data-testid={`admin-plan-${p.id}-features-fr`} />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase text-muted-foreground">Features (EN) — one per line</Label>
                  <Textarea rows={6} className="mt-1 rounded-sm font-mono text-xs" value={featuresEnText} onChange={(e) => setField(p.id, "features_en", e.target.value)} data-testid={`admin-plan-${p.id}-features-en`} />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button disabled={!isDirty || saving[p.id]} onClick={() => save(p.id)} className="rounded-sm bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-2" data-testid={`admin-plan-${p.id}-save`}>
                  <Save className="h-4 w-4" /> {saving[p.id] ? "..." : "Enregistrer"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
